import { observable, transaction, makeObservable } from "mobx"
import { BaseStore } from "stores/base.store"
import {
  getAllGameModeMultipliers,
  getGameModeMultipliersByGame,
  createGameModeMultiplier,
  updateGameModeMultiplier,
  deleteGameModeMultiplier,
  bulkUpsertGameModeMultipliers,
} from "../api/game-mode-multipliers.call"
import { GameModeMultiplier, CreateGameModeMultiplierDto, UpdateGameModeMultiplierDto, BulkUpsertGameModeMultipliersDto } from "./types"

export class GameModeMultipliersStore extends BaseStore {
  @observable
  private _allMultipliers: GameModeMultiplier[] = []

  @observable
  private _gameMultipliers: GameModeMultiplier[] = []

  @observable
  public currentGameId: string | null = null

  @observable
  public isLoading: boolean = false

  constructor(rootStore: any) {
    super(rootStore)
    makeObservable(this)
  }

  public async getAllGameModeMultipliers(): Promise<GameModeMultiplier[]> {
    this.resetFuncAfterSuccess()

    const res = await this.defaultFunction(getAllGameModeMultipliers)

    if (res) {
      transaction(() => {
        this._allMultipliers = Array.isArray(res) ? res : []
      })
    }

    return res
  }

  public async getGameModeMultipliersByGame(gameId: string): Promise<GameModeMultiplier[]> {
    this.resetFuncAfterSuccess()

    const res = await this.defaultFunction(getGameModeMultipliersByGame, gameId)

    if (res) {
      transaction(() => {
        this._gameMultipliers = Array.isArray(res) ? res : []
        this.currentGameId = gameId
      })
    }

    return res
  }

  public clearGameFilter() {
    this.currentGameId = null
    this._gameMultipliers = []
  }

  public async createGameModeMultiplier(data: CreateGameModeMultiplierDto): Promise<GameModeMultiplier> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      if (this.currentGameId) {
        await this.getGameModeMultipliersByGame(this.currentGameId)
      } else {
        await this.getAllGameModeMultipliers()
      }
    }

    const res = await this.defaultFunction(createGameModeMultiplier, data)

    return res
  }

  public async updateGameModeMultiplier(id: string, data: UpdateGameModeMultiplierDto): Promise<GameModeMultiplier> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      if (this.currentGameId) {
        await this.getGameModeMultipliersByGame(this.currentGameId)
      } else {
        await this.getAllGameModeMultipliers()
      }
    }

    const res = await this.defaultFunction(updateGameModeMultiplier, id, data)

    return res
  }

  public async deleteGameModeMultiplier(id: string): Promise<void> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      if (this.currentGameId) {
        await this.getGameModeMultipliersByGame(this.currentGameId)
      } else {
        await this.getAllGameModeMultipliers()
      }
    }

    await this.defaultFunction(deleteGameModeMultiplier, id)
  }

  public async bulkUpsertGameModeMultipliers(data: BulkUpsertGameModeMultipliersDto): Promise<GameModeMultiplier[]> {
    this.resetFuncAfterSuccess()

    this.funcAfterSuccess = async() => {
      if (this.currentGameId) {
        await this.getGameModeMultipliersByGame(this.currentGameId)
      } else {
        await this.getAllGameModeMultipliers()
      }
    }

    const res = await this.defaultFunction(bulkUpsertGameModeMultipliers, data)

    return res
  }

  get allMultipliers(): GameModeMultiplier[] {
    return Array.isArray(this._allMultipliers) ? this._allMultipliers : []
  }

  get gameMultipliers(): GameModeMultiplier[] {
    return Array.isArray(this._gameMultipliers) ? this._gameMultipliers : []
  }

  get multipliers(): GameModeMultiplier[] {
    if (this.currentGameId !== null) {
      return Array.isArray(this._gameMultipliers) ? this._gameMultipliers : []
    }

    return Array.isArray(this._allMultipliers) ? this._allMultipliers : []
  }
}
